import type { WaitForCallsStatusParameters } from 'viem/actions'

export const SAFE_CONNECTOR_ID = 'safe'
export const TEMPO_CONNECTOR_ID = 'xyz.tempo'

/**
 * Safe batches its own transactions; Tempo Accounts prepares its own envelope (P256/WebAuthn
 * signature plus any pending keyAuthorization) and needs viem's wallet-fill gas path — a raw
 * public-client estimate skips that fill and can under-fund the authorization, reverting out of gas.
 */
export function walletManagesGas({
  connectorId,
  isSafeWallet
}: {
  connectorId: string | undefined
  isSafeWallet: boolean
}): boolean {
  return isSafeWallet || connectorId === TEMPO_CONNECTOR_ID
}

/**
 * EIP-5792 final shape (`atomic`), plus the pre-final `atomicBatch` shape only when
 * `acceptLegacyAtomicBatch` is set. The legacy shape is only trusted for the Safe
 * Apps connector (`connector.id === 'safe'`): we have no data on which other wallets
 * still report it or whether they accept viem's `wallet_sendCalls` 2.0.0 payload.
 */
export function supportsAtomicBatching(
  capabilities: {
    atomic?: { status: string } | undefined
    atomicBatch?: { supported: boolean } | undefined
  },
  { acceptLegacyAtomicBatch = false }: { acceptLegacyAtomicBatch?: boolean } = {}
): boolean {
  if (capabilities.atomic?.status === 'supported') return true
  return acceptLegacyAtomicBatch && capabilities.atomicBatch?.supported === true
}

// Safe's provider returns 200 with no receipts for the few seconds between execution and receipt availability.
export const hasReceiptOrFailed: WaitForCallsStatusParameters['status'] = ({
  statusCode,
  receipts
}) => (statusCode === 200 || statusCode === 500 ? (receipts?.length ?? 0) > 0 : statusCode >= 300)
